/**
 * External store fed by the socket. Components read it with
 * useSyncExternalStore, so React never has to be told about the socket.
 */
export type ConnectionStatus =
  | 'idle'
  /** No key stored in this browser at all. */
  | 'needs-credential'
  /** Keys are stored, the owner deliberately left the console. */
  | 'signed-out'
  /** A key is stored but not unlocked: waiting for the owner to press «Войти». */
  | 'locked'
  | 'connecting'
  | 'authenticating'
  | 'ready'
  | 'offline'
  /** The server refused the key we presented. */
  | 'auth-failed';

export interface TransportState {
  status: ConnectionStatus;
  owner: string | null;
  kid: string | null;
  label: string | null;
  /** Human-readable reason shown when status is auth-failed or offline. */
  message: string | null;
  /** Which stored key the server refused, so the UI can point at it. */
  rejectedKid: string | null;
  reconnectInMs: number | null;
  attempt: number;
}

const initial: TransportState = {
  status: 'idle',
  owner: null,
  kid: null,
  label: null,
  message: null,
  rejectedKid: null,
  reconnectInMs: null,
  attempt: 0,
};

let state: TransportState = initial;
const listeners = new Set<() => void>();

export function getState(): TransportState {
  return state;
}

/** Stable snapshot for SSR: the server has no socket and never will. */
export function getServerState(): TransportState {
  return initial;
}

export function subscribeState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setState(patch: Partial<TransportState>): void {
  const next = { ...state, ...patch };
  const changed = (Object.keys(patch) as (keyof TransportState)[])
    .some((k) => state[k] !== next[k]);
  if (!changed) return;
  state = next;
  for (const listener of listeners) listener();
}

export function resetState(): void {
  state = initial;
  for (const listener of listeners) listener();
}
